/**
 * Pull — fetch sessions from the server into the local Dexie repo.
 *
 * Phase 1: full pull (no since-cursor). Documented as a limitation in
 * shared-docs/offline-first-architecture.md — fine up to a few hundred
 * sessions per user.
 */

import * as sessionsRepo from '../db/sessions';
import { setMeta } from '../db/meta';
import { withTimeout, PULL_TIMEOUT_MS } from '../network';
import { getSessions as apiGetSessions } from '../api';
import { fromApiRow } from './adapter';
import useNudgeStore from '../../stores/useNudgeStore';

/**
 * Pull all sessions for the authenticated user.
 *
 * @param {string} userId — Cognito sub (we still rekey local-only rows on
 *                          sign-in via migrateAnonymousToUser before pulling)
 * @returns {Promise<number>} count of rows upserted
 */
export async function pullSessions(userId) {
  if (!userId) return 0;
  const response = await withTimeout(apiGetSessions(), PULL_TIMEOUT_MS, 'pullSessions');

  // Channel 2 — the launch-time /sessions response may carry an in-app
  // re-engagement notice; surface it to the app shell (no new API routes).
  const inAppNudge = response?.inAppNudge || response?.data?.inAppNudge || null;
  try {
    useNudgeStore.getState().setNudge(inAppNudge);
  } catch {
    // non-fatal — nudge display must never break the sync path
  }

  const items = response?.data?.sessions || response?.data || response?.sessions || response || [];
  let upserted = 0;
  for (const remote of Array.isArray(items) ? items : []) {
    const local = fromApiRow(remote);
    if (!local) continue;
    await sessionsRepo.upsertFromServer(local, userId);
    upserted += 1;
  }
  await setMeta('lastSyncAt', new Date().toISOString());
  return upserted;
}
